// @ts-nocheck

class BdMenu extends HTMLElement {
  connectedCallback() {
    this.triggers = this.querySelectorAll('[data-submenu]');
    this.panels = this.querySelectorAll('[data-submenu-id]');

    this.isTouch = matchMedia('(hover: none)').matches;
    this.currentOpen = null;

    if (!this.isTouch) {
      this.addEventListener('pointerover', this.onHoverEnter);

      this.addEventListener('pointerleave', this.onHoverLeave, true);
    }

    this.addEventListener('click', this.onClick);
    this.addEventListener('focusin', this.onFocusIn, true);
  }

  /* Accessible Keyboard Targeting */
  onFocusIn = (event) => {
    if (this.isTouch) return;

    const trigger = event.target.closest('[data-submenu]');
    const panel = event.target.closest('[data-submenu-id]');

    // 1. Focus moved outside the entire menu → close everything
    if (!this.contains(event.target)) {
      this.closeAll();
      return;
    }

    // 2. Focus is inside the currently open panel → keep it open
    if (panel && panel.dataset.submenuId === this.currentOpen) {
      return;
    }

    // 3. Focused a top-level item WITH submenu → open it (and close previous)
    if (trigger && trigger.dataset.submenu) {
      const id = trigger.dataset.submenu;
      if (this.currentOpen !== id) {
        this.open(id);
      }
      return;
    }

    // 4. Focus is inside the menu, but:
    //    - not in an open panel
    //    - not on a trigger with submenu
    //    (e.g. "Catalog" / "Contact" links)
    this.closeAll();
  };

  // -----------------------------
  // Active Trigger Helpers
  // -----------------------------
  clearActiveTriggers() {
    this.triggers.forEach((t) => t.classList.remove('is-active'));
  }

  setActiveTrigger(id) {
    this.clearActiveTriggers();
    const trigger = this.querySelector(`[data-submenu="${id}"]`);
    if (trigger) trigger.classList.add('is-active');
  }

  // -----------------------------
  // Desktop Hover
  // -----------------------------

  onHoverEnter = (event) => {
    if (this.isTouch) return;

    clearTimeout(this.closeTimeout);

    const menuItem = event.target.closest('.menu-item');
    if (!menuItem) return;

    const trigger = menuItem.querySelector('[data-submenu]');
    const id = trigger?.dataset.submenu;

    //  Hovered item has NO submenu → close everything
    if (!id) {
      this.closeAll();
      return;
    }

    //   Hovered item has submenu → switch to it
    if (this.currentOpen && this.currentOpen !== id) {
      this.close(this.currentOpen);
    }

    this.open(id);
  };

  onHoverLeave = (event) => {
    if (this.isTouch) return;

    // If pointer is still inside the menu, do nothing
    if (event.relatedTarget && this.contains(event.relatedTarget)) {
      return;
    }

    // Start delayed close
    clearTimeout(this.closeTimeout);
    this.closeTimeout = setTimeout(() => {
      // Only close if STILL outside the menu
      if (!this.matches(':hover')) {
        this.closeAll();
      }
    }, 250);
  };

  // -----------------------------
  // Mobile Tap
  // -----------------------------
  onClick = (event) => {
    const trigger = event.target.closest('[data-submenu]');
    if (!trigger) return;

    const id = trigger.dataset.submenu;
    if (!id) return;

    const panel = this.querySelector(`[data-submenu-id="${id}"]`);
    if (!panel) return;

    if (this.isTouch) {
      event.preventDefault();
      this.toggle(id);
    }
  };

  // -----------------------------
  // Core Logic
  // -----------------------------
  open(id) {
    this.closeAll();
    this.currentOpen = id;
    this.setActiveTrigger(id);

    const panel = this.querySelector(`[data-submenu-id="${id}"]`);
    if (!panel) return;

    panel.dataset.open = 'true';
    panel.style.setProperty('--submenu-height', panel.scrollHeight + 'px');

    // Make submenu items tabbable
    panel.querySelectorAll('a, button').forEach((el) => (el.tabIndex = 0));
  }

  closeAll() {
    this.currentOpen = null;

    this.panels.forEach((panel) => {
      panel.dataset.open = 'false';
      panel.style.setProperty('--submenu-height', '0px');

      // Remove from tab order
      panel.querySelectorAll('a, button').forEach((el) => (el.tabIndex = -1));
    });

    this.clearActiveTriggers();
  }

  close(id) {
    const panel = this.querySelector(`[data-submenu-id="${id}"]`);
    if (!panel) return;

    panel.dataset.open = 'false';
    panel.style.setProperty('--submenu-height', '0px');

    const trigger = this.querySelector(`[data-submenu="${id}"]`);
    if (trigger) trigger.classList.remove('is-active');
  }

  toggle(id) {
    const panel = this.querySelector(`[data-submenu-id="${id}"]`);
    if (!panel) return;

    const isOpen = panel.dataset.open === 'true';
    isOpen ? this.close(id) : this.open(id);
  }
}

customElements.define('bd-menu', BdMenu);

/** DRAWER MENU ***/
class BdDrawerMenu extends HTMLElement {
  connectedCallback() {
    this.panelStack = [];

    this.menuHandle = this.dataset.menu;

    // Find the matching hamburger
    this.hamburger = document.querySelector(
      `[data-mobile-open][data-menu="${this.menuHandle}"]`,
    );

    if (this.hamburger) {
      this.hamburger.addEventListener('click', () => this.toggleDrawer());
    }

    // Close button
    this.closeButton = this.querySelector('[data-close]');
    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => this.closeDrawer());
    }

    // Panels + triggers
    this.triggers = this.querySelectorAll('[data-submenu]');
    this.panels = this.querySelectorAll('[data-submenu-id]');
    this.backButtons = this.querySelectorAll('[data-back]');

    this.triggers.forEach((trigger) => {
      trigger.addEventListener('click', () => {
        const id = trigger.dataset.submenu;
        this.openPanel(id);
      });
    });

    this.backButtons.forEach((btn) => {
      btn.addEventListener('click', () => this.closeCurrentPanel());
    });

    // Bind handlers so we can remove them later
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onClickOutside = this.onClickOutside.bind(this);
  }

  // ------------------------------------------------------------
  // Drawer Open / Close
  // ------------------------------------------------------------

  toggleDrawer() {
    const isOpen = this.dataset.open === 'true';
    isOpen ? this.closeDrawer() : this.openDrawer();
  }

  openDrawer() {
    this.dataset.open = 'true';
    if (this.hamburger) this.hamburger.dataset.open = 'true';
    document.body.style.overflow = 'hidden';

    // Add global listeners
    document.addEventListener('keydown', this.onKeyDown);
    document.addEventListener('click', this.onClickOutside);
  }

closeDrawer() {
  // Start closing animation
  this.dataset.closing = 'true';
  this.dataset.open = 'false';
  if (this.hamburger) this.hamburger.dataset.open = 'false';

  const menuList = this.querySelector('.menu-list');

  const onEnd = (event) => {
    if (event.target !== menuList) return;

    // Cleanup after animation
    delete this.dataset.closing;
    document.body.style.overflow = '';

    document.removeEventListener('keydown', this.onKeyDown);
    document.removeEventListener('click', this.onClickOutside);

    this.panels.forEach(panel => panel.removeAttribute('data-open'));
    this.panelStack = [];

    menuList.removeEventListener('transitionend', onEnd);
  };

  menuList.addEventListener('transitionend', onEnd);
}




  // ------------------------------------------------------------
  // Close on ESC
  // ------------------------------------------------------------

  onKeyDown(event) {
    if (event.key === 'Escape') {
      this.closeDrawer();
    }
  }

  // ------------------------------------------------------------
  // Click Outside to Close
  // ------------------------------------------------------------

  onClickOutside(event) {
    const nav = this.querySelector('.menu-list');

    // If click is inside the nav panel → do nothing
    if (nav.contains(event.target)) return;

    // If click is on the hamburger → ignore
    if (
      event.target === this.hamburger ||
      this.hamburger?.contains(event.target)
    ) {
      return;
    }

    this.closeDrawer();
  }

  // ------------------------------------------------------------
  // Submenu Layer Navigation
  // ------------------------------------------------------------

openPanel(id) {
  const panel = this.querySelector(`[data-submenu-id="${id}"]`);
  if (!panel) return;

  // Mark panel open
  panel.dataset.open = 'true';

  // Push onto stack
  this.panelStack.push(panel);
}


closeCurrentPanel() {
  // Pop the last opened panel
  const panel = this.panelStack.pop();
  if (panel) {
    panel.removeAttribute('data-open');
  }
}


}

customElements.define('bd-drawer-menu', BdDrawerMenu);
